import { InputField } from '@components/utility/inputs';
import { useAppSelector } from '@hooks/generic';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgress, Grid2, InputAdornment } from '@mui/material';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { ProofOfOwnershipAlert } from '@rla/components/alerts';
import { SERIAL_NUMBER_REGEX } from '@rla/constants';
import { RLAFormFooter } from '@rla/routes/RLAForm/RLAFormFooter';
import { selectValidateSerialNumber } from '@rla/services';
import { PinPukRecoveryForm, RLAFormMode } from '@rla/types';
import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';

export const RLAPinForm = () => {
    const intl = useIntl();

    const formik = useFormikContext<PinPukRecoveryForm>();
    const serialValidationState = useAppSelector((state) =>
        selectValidateSerialNumber({ serialNumber: formik.values.product_serial_number })(state)
    );

    const serialNumberIsLoading = serialValidationState.status === QueryStatus.pending;
    const serialNumberValidationComplete =
        formik.touched.product_serial_number && !serialNumberIsLoading && Boolean(formik.values.product_serial_number);
    const serialNumberIsValid =
        serialNumberValidationComplete &&
        SERIAL_NUMBER_REGEX.test(formik.values.product_serial_number) &&
        !formik.errors.product_serial_number;

    return (
        <>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="first_name"
                    name="first_name"
                    placeholder={intl.formatMessage({
                        id: 'common.firstName.placeholder',
                        defaultMessage: 'Enter first name',
                    })}
                    topLabel={intl.formatMessage({ id: 'common.firstName', defaultMessage: 'First Name' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="last_name"
                    name="last_name"
                    placeholder={intl.formatMessage({
                        id: 'common.lastName.placeholder',
                        defaultMessage: 'Enter last name',
                    })}
                    topLabel={intl.formatMessage({ id: 'common.lastName', defaultMessage: 'Last Name' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="email"
                    name="email"
                    placeholder={intl.formatMessage({
                        id: 'common.email.placeholder',
                        defaultMessage: 'Enter email',
                    })}
                    topLabel={intl.formatMessage({ id: 'common.email', defaultMessage: 'Email' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="distributor_name"
                    name="distributor_name"
                    placeholder={intl.formatMessage({
                        id: 'common.distributorOrSalesManager.placeholder',
                        defaultMessage: 'Enter distributor or sales manager',
                    })}
                    topLabel={intl.formatMessage({
                        id: 'common.distributorOrSalesManager',
                        defaultMessage: 'Victron Distributor or Sales Manager',
                    })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={12}>
                <ProofOfOwnershipAlert mode={RLAFormMode.PIN_PUK_RECOVERY} />
            </Grid2>
            <Grid2 size={12}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="product_serial_number"
                    name="product_serial_number"
                    placeholder={intl.formatMessage({
                        id: 'common.serialNumber.placeholder',
                        defaultMessage: 'Enter serial number e.g HQ123456789',
                    })}
                    topLabel={intl.formatMessage({
                        id: 'common.productSerialNumber',
                        defaultMessage: 'Product Serial Number',
                    })}
                    onChange={(event) => {
                        const isValidFormat = SERIAL_NUMBER_REGEX.test(event.target.value);

                        if (isValidFormat) {
                            formik.setFieldError('product_serial_number', undefined);
                        }
                    }}
                    slotProps={{
                        input: {
                            endAdornment: serialNumberIsLoading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ) : serialNumberIsValid ? (
                                <InputAdornment position="end">
                                    <CheckCircleIcon color="success" fontSize="small" />
                                </InputAdornment>
                            ) : serialNumberValidationComplete ? (
                                <InputAdornment position="end">
                                    <CancelIcon color="error" fontSize="small" />
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                    debounceMs={0}
                />
            </Grid2>
            <RLAFormFooter mode={RLAFormMode.PIN_PUK_RECOVERY} />
        </>
    );
};
